import ufeApi from 'services/api/ufeApi';
import UUIDv4 from 'utils/UUID';
import Hashing from 'utils/Hashing';
import authenticationUtils from 'utils/Authentication';
import Storage from 'utils/localStorage/Storage';
import LOCAL_STORAGE from 'utils/localStorage/Constants';
import GenerateTokens from 'services/api/authentication/generateLithiumSsoToken';
import RCPSCookies from 'utils/RCPSCookies';
import { URLS, AUTH_HEADERS } from 'constants/authentication';
import { MFA_MODE } from 'constants/phoneVerification';
import { setAuthOperationInProgress } from 'services/auth/newFlow/authFlow';
import BeyondIdentityUtils from 'utils/BeyondIdentity';
import PasskeyService from 'services/PasskeyService';

const { sha256 } = Hashing;
const { isAuthServiceEnabled, getFingerPrint } = authenticationUtils;
const { generateTokens } = GenerateTokens;

// https://jira.sephora.com/wiki/display/ILLUMINATE/Login+API

function login({ deviceFingerprint, login: log1n, password, options }) {
    // Set flag to skip auth flow during login
    // cookieBasedAuth not to trigger authFlow.execute during generateTokens call
    setAuthOperationInProgress(true);

    const {
        loginForCheckout, isKeepSignedIn, isOrderConfirmation, biAccountInfo, extraParams: allExtraParams
    } = options;
    const { isCheckoutInitAttempt, headerValue, ...extraParams } = allExtraParams || {};

    let body = {
        login: log1n,
        password,
        ...(isKeepSignedIn && deviceFingerprint && { deviceFingerprint })
    };

    if (isOrderConfirmation) {
        body.source = 'orderConfirmation';

        if (biAccountInfo) {
            body.isJoinBi = biAccountInfo.isJoinBi;
            body.biAccount = biAccountInfo.birthday;
            body.subscription = biAccountInfo.subscription;
        }
    } else {
        body.loginForCheckout = loginForCheckout;
        body.isKeepSignedIn = isKeepSignedIn;
    }

    if (Object.keys(extraParams).length) {
        body = {
            ...body,
            ...extraParams
        };
    }

    // Passkey flow: ensure the local device id is not sent to backend
    if (extraParams?.isPasskeyLogin && 'id' in body) {
        delete body.id;
    }

    if (isAuthServiceEnabled()) {
        body.email = body.login;
        delete body.login;

        body.keepSignedIn = body.isKeepSignedIn;
        delete body.isKeepSignedIn;

        const randomNumber = UUIDv4();

        return sha256(randomNumber).then(async function (digest) {
            const token = digest;
            body.randomNumber = token;
            let url = URLS.ATG_LOGIN_URL;
            const headers = AUTH_HEADERS;
            const xCausedHeader = headerValue || location.href;
            headers['X-CAUSED-BY-URL'] = xCausedHeader;

            if (RCPSCookies.isRCPSAuthEnabled()) {
                url = URLS.SDN_LOGIN_URL;
                headers.deviceId = await getFingerPrint();
            }

            const performLoginRequest = async () => {
                return ufeApi
                    .makeRequest(url, {
                        method: 'POST',
                        body: JSON.stringify(body),
                        headers
                    })
                    .then(async tokenData => {
                        if (tokenData?.errors) {
                            return Promise.reject(tokenData.errors[0]);
                        }

                        if (tokenData?.responseStatus >= 400) {
                            return Promise.reject(tokenData);
                        }

                        if (Sephora.configurationSettings?.enableStepUpAuthFlowBP2 && tokenData?.mfaEnabled) {
                            const mfaChallengeOptions = {};

                            if (tokenData?.mfaMode === MFA_MODE.PHONE) {
                                mfaChallengeOptions['shouldTriggerPhoneMFA'] = true;
                                mfaChallengeOptions['phoneLast4Digits'] = tokenData?.phoneLast4Digits;
                            }

                            tokenData?.mfaMode === MFA_MODE.EMAIL && (mfaChallengeOptions.shouldTriggerEmailMfa = true);

                            return Promise.resolve({
                                mfaEnabled: true,
                                ...mfaChallengeOptions
                            });
                        }

                        if (tokenData?.isStoreBiMember && Sephora.configurationSettings?.isEmailVerificationEnabled) {
                            return Promise.resolve({
                                shouldTriggerEmailVerification: true,
                                isStoreBiMember: true
                            });
                        }

                        if (tokenData?.profileWarnings) {
                            Storage.local.setItem(LOCAL_STORAGE.LOGIN_PROFILE_WARNINGS, tokenData?.profileWarnings);

                            if (isCheckoutInitAttempt) {
                                Storage.local.setItem(LOCAL_STORAGE.POSTPONE_LOGIN_PROFILE_WARNINGS, true);
                            }
                        }

                        body.token = tokenData.token;
                        body.randomNumber = randomNumber;

                        const generateTokensResponse = await generateTokens(body, { headerValue: xCausedHeader });

                        return {
                            ...generateTokensResponse,
                            hasIdentity: tokenData.hasIdentity
                        };
                    })
                    .finally(() => {
                        // Clear flag when login completes (success or error)
                        setAuthOperationInProgress(false);
                    });
            };

            if (!extraParams?.isPasskeyLogin || !extraParams?.passkeyId) {
                return performLoginRequest();
            }

            // Passkey login flow
            if (extraParams?.isPasskeyLogin && extraParams?.passkeyId) {
                try {
                    // Use SHA-256 digest as state
                    const { authenticateUrl, state } = await PasskeyService.generateAuthenticateUrl(body.randomNumber);

                    // Authenticate with SDK for the specific passkey
                    let authResult;

                    try {
                        authResult = await BeyondIdentityUtils.authenticate(authenticateUrl, extraParams.id);
                    } catch (authError) {
                        Sephora.logger?.error('Passkey authenticate failed', { error: authError });

                        throw authError;
                    }

                    // Parse redirect URL returned by SDK
                    const urlParams = PasskeyService.parseRedirectUrl(authResult.redirectUrl);

                    // Build API URL for passkey login (dynamic host via ufeApi)
                    const queryParams = new URLSearchParams({
                        code: urlParams.code,
                        state: urlParams.state || state,
                        redirectUrl: PasskeyService.getRedirectUri(),
                        identityId: extraParams.identityId,
                        passkeyId: extraParams.passkeyId
                    });

                    url = `/gway/v1/dotcom/auth/passkeylogin?${queryParams.toString()}`;
                } catch (error) {
                    // Return error with special flag indicating passkey was cancelled
                    setAuthOperationInProgress(false);

                    return Promise.reject(
                        Object.assign(new Error('Passkey authentication was cancelled. Please sign in with your password.'), {
                            errorCode: 'PASSKEY_CANCELLED',
                            errorMessages: ['Passkey authentication was cancelled. Please sign in with your password.'],
                            passkeyFailed: true
                        })
                    );
                }
            }

            return performLoginRequest();
        });
    }

    const url = '/api/auth/login';

    return ufeApi
        .makeRequest(url, {
            method: 'POST',
            body: JSON.stringify(body),
            headers: {
                'Content-type': 'application/json',
                'x-requested-source': 'web'
            }
        })
        .then(data => (data.errorCode ? Promise.reject(data) : data))
        .finally(() => {
            // Clear flag when login completes (success or error)
            setAuthOperationInProgress(false);
        });
}

export default { login };
